import { isAnyCarsVehiclesCategory } from '@kijiji/category'
import { type ListingFlags, AdSource } from '@kijiji/generated/graphql-types'
import { formatWholeNumber } from '@kijiji/number/formatWholeNumber'
import { type TFunction } from 'next-i18next'

import { TRANSLATION_KEYS } from '@/constants/localization'
import { ATTRIBUTES } from '@/features/attributes/constants/attributes'
import { DEALER_UPDATES_FIELDS } from '@/features/attributes/constants/attributesDealerUpdates'
import { getTransmissionTranslation } from '@/features/attributes/utils/getTransmissionTranslation'
import { type RouteLocale, ROUTE_LOCALE } from '@/hooks/useLocale'

export const FLAGS = {
  CATEGORY_BADGE: 'categorySpecificBadge',
  EXTERNAL_LISTING: 'externalListingOnly',
  HIGHLIGHT: 'highlight',
  PRICE_DROP: 'priceDrop',
  SHOWCASE: 'showcase',
  TOP_AD: 'topAd',
  SHIPPING: 'shippedBySeller',
  VIRTUAL_TOUR: 'virtualTour',
  PROMO_TOP_AD: 'isPromotionTopAd',
  PROMO_PROV_TOP_AD: 'isPromotionProvTopAd',
  FINANCING: 'financingAvailable',
}

export const SRP_ATTRIBUTES = {
  LOGO: 'srpLogoUrl',
}

/**
 * Defines if listing has a priceDrop
 * */
export const shouldShowPriceDrop = (
  hasPriceDropFlag: boolean,
  originalPriceAmount?: number
): boolean => {
  if (!hasPriceDropFlag) return false
  if (!originalPriceAmount) return false

  return true
}

type AutosAttributes = {
  carfax?: boolean
  cpo?: boolean
  dealerUpdates?: string[]
  isNew?: boolean
  mileage?: number
  transmission?: number
}

/**
 * Defines Autos attributes for listing:
 * - Transmission type : 1-Manual / 2-Automatic
 * - Mileage in km
 * - Dealer updates - It will return key related to the translation
 * */
export const getAutosAttributes = (
  attributesDictionary: Record<string, string[]>
): AutosAttributes => {
  // Transmission attribute
  const transmission = parseInt(attributesDictionary[ATTRIBUTES.CAR_TRANSMISSION]?.[0] || '0')

  // Mileage attribute in km
  const mileage = parseInt(attributesDictionary[ATTRIBUTES.CAR_MILEAGE]?.[0] || '0')

  // Carfax link
  const carfax = !!attributesDictionary[ATTRIBUTES.CARFAX]?.[0]

  // Certified pre-owned
  const cpo = !!attributesDictionary[ATTRIBUTES.CPO]?.[0]

  // Dealer updates
  const dealerUpdates = Object.keys(DEALER_UPDATES_FIELDS).reduce(
    (acc: string[], canonicalName: string) => {
      const values = attributesDictionary[canonicalName] ?? []
      if (values.length) return [...acc, canonicalName]
      return acc
    },
    []
  )

  // Vehicle type
  const isNew = attributesDictionary[ATTRIBUTES.VEHICLE_TYPE]?.[0] === 'new'

  return { transmission, mileage, carfax, cpo, dealerUpdates, isNew }
}

export const getCarsVehiclesAttributes = (
  attributesDictionary: Record<string, string[]>,
  categoryId: number,
  routeLocale: RouteLocale,
  t: TFunction
) => {
  // Early return if it is not Autos category
  if (!isAnyCarsVehiclesCategory(categoryId)) {
    return {
      carfax: false,
      carsVehiclesAttributes: [],
      cpo: false,
      dealerUpdates: [],
      isNew: false,
    }
  }

  // Array of texts that will be displayed side by side in the listing card
  const carsVehiclesAttributes = []
  let translatedDealerUpdates: string[] = []

  // Get car attributes
  const {
    carfax = false,
    cpo = false,
    dealerUpdates = [],
    isNew = false,
    mileage,
    transmission,
  } = getAutosAttributes(attributesDictionary)

  if (transmission) {
    const transmissionKey = getTransmissionTranslation(transmission)
    if (transmissionKey) carsVehiclesAttributes.push(t(transmissionKey))
  }

  if (mileage) {
    const formattedMileage = formatWholeNumber(mileage, routeLocale === ROUTE_LOCALE.en)
    carsVehiclesAttributes.push(`${formattedMileage} km`)
  }

  if (dealerUpdates.length) {
    translatedDealerUpdates = dealerUpdates.map((item) =>
      t(`${TRANSLATION_KEYS.LISTING}:attributes.dealer_updates.${item}`)
    )
  }

  return { carfax, cpo, carsVehiclesAttributes, dealerUpdates: translatedDealerUpdates, isNew }
}

/**
 * Get the ad source for a promotion listing based on the flags.
 * @param {ListingFlag[]} flags - The flags associated with the listing.
 * @param {string} [adSource] - The ad source value. Optional.
 * @param {boolean} [isSeeAllTopAd] - Indicates if it is a "See All Top Ad" scenario. Optional.
 * @returns The ad source for the promotion listing, or an empty string if no ad source is found.
 *
 * @example const flags = [
 *     { name: 'PROMO_TOP_AD', value: true },
 *     { name: 'SOME_FLAG', value: false },
 *   ];
 *   const adSource = 'SomeAdSource';
 *   const isSeeAllTopAd = false;
 *   const adSourceForPromotion = getAdSourceForPromotionListing(flags, adSource, isSeeAllTopAd);
 *   console.log(adSourceForPromotion); // Output: 'TOP_AD'
 */
export const getAdSourceForPromotionListing = (
  flags: ListingFlags,
  adSource?: AdSource,
  isSeeAllTopAd?: boolean
) => {
  if (adSource && adSource === AdSource.Organic && !isSeeAllTopAd) {
    return ''
  }

  if (flags.isPromotionTopAd || flags.topAd || adSource === AdSource.TopAd) {
    return AdSource.TopAd
  } else if (flags.isPromotionProvTopAd) {
    return AdSource.ProvTopAd
  } else {
    return ''
  }
}
